import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { PermissionRoute, ProtectedRoute } from './guards';
import { AppLayout } from '@layouts/AppLayout';
import { AuthLayout } from '@layouts/AuthLayout';
import { PageLoading } from '@components/common/PageLoading';
import { PERMISSIONS } from '@/types';
import { ROUTES } from './routes';

const LoginPage = lazy(() => import('@pages/LoginPage'));
const DashboardListPage = lazy(() => import('@pages/DashboardListPage'));
const DashboardDesignerPage = lazy(() => import('@pages/DashboardDesignerPage'));
const DashboardViewerPage = lazy(() => import('@pages/DashboardViewerPage'));
const UsersPage = lazy(() => import('@pages/UsersPage'));
const RolesPage = lazy(() => import('@pages/RolesPage'));
const PermissionsPage = lazy(() => import('@pages/PermissionsPage'));
const ReportsPage = lazy(() => import('@pages/ReportsPage'));
const AuditLogsPage = lazy(() => import('@pages/AuditLogsPage'));
const SettingsPage = lazy(() => import('@pages/SettingsPage'));
const ProfilePage = lazy(() => import('@pages/ProfilePage'));
const ForbiddenPage = lazy(() => import('@pages/ForbiddenPage'));
const NotFoundPage = lazy(() => import('@pages/NotFoundPage'));

export function AppRouter() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route element={<AuthLayout />}>
          <Route path={ROUTES.login} element={<LoginPage />} />
        </Route>

        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to={ROUTES.dashboards} replace />} />
          <Route path={ROUTES.dashboards} element={<DashboardListPage />} />
          <Route
            path={ROUTES.dashboardDesigner()}
            element={
              <PermissionRoute permission={PERMISSIONS.DASHBOARD_EDIT}>
                <DashboardDesignerPage />
              </PermissionRoute>
            }
          />
          <Route path={ROUTES.dashboardViewer()} element={<DashboardViewerPage />} />

          <Route
            path={ROUTES.users}
            element={
              <PermissionRoute permission={PERMISSIONS.USERS_MANAGE}>
                <UsersPage />
              </PermissionRoute>
            }
          />
          <Route
            path={ROUTES.roles}
            element={
              <PermissionRoute permission={PERMISSIONS.ROLES_MANAGE}>
                <RolesPage />
              </PermissionRoute>
            }
          />
          <Route
            path={ROUTES.permissions}
            element={
              <PermissionRoute permission={PERMISSIONS.ROLES_MANAGE}>
                <PermissionsPage />
              </PermissionRoute>
            }
          />
          <Route
            path={ROUTES.reports}
            element={
              <PermissionRoute permission={PERMISSIONS.REPORTS_VIEW}>
                <ReportsPage />
              </PermissionRoute>
            }
          />
          <Route
            path={ROUTES.audit}
            element={
              <PermissionRoute permission={PERMISSIONS.AUDIT_VIEW}>
                <AuditLogsPage />
              </PermissionRoute>
            }
          />
          <Route path={ROUTES.settings} element={<SettingsPage />} />
          <Route path={ROUTES.profile} element={<ProfilePage />} />
          <Route path={ROUTES.forbidden} element={<ForbiddenPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
